import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import UserMultiselectDropdown from '$tablet-app/src/app/components/UserMultiselectDropdown';
import { palette } from '$tablet-app/src/app/theme';
import { useCurrentUser } from '$tablet-app/src/app/users';
import { excludeUserFromList } from '$tablet-app/src/app/users/functions';
import { sticky_note_color_choices } from '@akin-home/sticky-notes';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { FC, useState } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import styled from 'styled-components/native';
import { font_variant_styles } from '../../theme';
import { useListCurrentHabitatUsers } from '../../users';

import {
  createStickyNote,
  filterUsersSharedForStickyNote,
  getStickyNoteFontStyle,
  toggleStickyNoteSharedWithIDs,
} from '../functions';
import { StickyNote } from '../types';
import StickyNoteSharedWithDisplay from './StickyNoteSharedWithDisplay';

interface Props {
  note?: StickyNote;

  onSave: (note: StickyNote) => void;
  onPressDelete: (id: string) => void;
}

const StickyNoteEditTile: FC<Props> = (props) => {
  const user = useCurrentUser();
  const { data: users } = useListCurrentHabitatUsers();
  const { habitatID } = user;

  /** The local note state */
  const [note, setNote] = useState(
    props.note ||
      createStickyNote({
        habitatID,
        draft: true,
        color: sticky_note_color_choices[0],
      })
  );
  const [height, setHeight] = useState<number>(56);

  const sharedWithUsers = filterUsersSharedForStickyNote(note, users);

  const updateStickyText = (content: string) => setNote({ ...note, content });
  const handleSaveClick = () => props.onSave(note);

  const onToggleSelectedID = (id: string) =>
    setNote(toggleStickyNoteSharedWithIDs(note, id));

  const updateColor = (color: string) => setNote({ ...note, color });

  const textStyle = { height, ...getStickyNoteFontStyle(note) };

  return (
    <StickyNoteTile key={note.id} color={note.color}>
      <UserMultiselectDropdown
        users={excludeUserFromList(users, user.id)}
        selected_ids={[user.id, ...(note.sharedWithIDs || [])]}
        onSelect={onToggleSelectedID}
        anchor={({ showPopUp }) => (
          <Row justifyContent="space-between">
            <TouchableOpacity onPress={showPopUp}>
              <StickyNoteSharedWithDisplay users={sharedWithUsers} />
            </TouchableOpacity>

            <NewButton
              icon="share-alt"
              variant="secondary"
              onPress={showPopUp}
            />
          </Row>
        )}
      />
      <TextWrapper>
        <NoteTextInput
          style={textStyle}
          onChangeText={updateStickyText}
          value={note.content}
          multiline
          onContentSizeChange={(e) => {
            setHeight(e.nativeEvent.contentSize.height);
          }}
          maxLength={100}
          autoFocus
        ></NoteTextInput>
      </TextWrapper>

      <Row justifyContent="space-between" align-items="center">
        <Row>
          {sticky_note_color_choices.map((color, idx) => (
            <React.Fragment key={color}>
              {idx !== 0 ? <Spacer width="16px" /> : null}
              <ColorChoice
                color={color}
                active={color === note.color}
                onPress={() => updateColor(color)}
              >
                {color === note.color ? (
                  <FontAwesomeIcon icon="check" color="#535AF1" size={20} />
                ) : null}
              </ColorChoice>
            </React.Fragment>
          ))}
        </Row>

        <View>
          <NewButton onPress={handleSaveClick}>Save</NewButton>
        </View>
      </Row>
    </StickyNoteTile>
  );
};

const NoteTextInput = styled(TextInput)`
  text-align: center;
  align-items: center;
  border: none;
  ${font_variant_styles.stickyNote.regular}
`;

const StickyNoteTileBase = styled.View`
  width: 450px;
  height: 450px;
  border-radius: 34px;

  margin: 10px;
  position: relative;
  padding: 20px;
`;

const StickyNoteTile = styled(StickyNoteTileBase)<{ color?: string }>`
  background-color: ${(props) => props.color || palette.pastel_green};
`;

const TextWrapper = styled.View`
  flex: 1;
  justify-content: center;
`;

const ColorChoice = styled.TouchableOpacity<{
  color: string;
  active?: boolean;
}>`
  height: 56px;
  width: 56px;

  justify-content: center;
  align-items: center;

  border-width: 2px;
  border-color: ${({ active }) => (active ? '#535AF1' : 'white')};
  border-radius: 20px;

  background-color: ${({ color }) => color};

  box-shadow: 0px 4px 8px rgba(16, 24, 40, 0.6);
`;

export default StickyNoteEditTile;
